import type { LucideIcon } from "lucide-react";
import {
  Home,
  Flag,
  Package,
  Smartphone,
  Link2,
  CircleHelp,
  Sparkles,
  Megaphone,
  Bell,
  ImageUp,
} from "lucide-react";
import { Constants } from "../constant";
import { canReadService, ServiceDomain, type UserRole } from "src/lib/roles";
import { hasPermission, Permission } from "src/lib/roles";

export interface AppNavItem {
  key: string;
  to: string;
  label: string;
  icon: LucideIcon;
  end?: boolean;
}

/**
 * Primary app navigation entries (sidebar / rail). Role-gated to match routes + Header behavior.
 */
export function getAppNavItems(role: UserRole): AppNavItem[] {
  const items: AppNavItem[] = [];

  items.push({
    key: "dashboard",
    to: Constants.DASHBOARD_ROUTE,
    label: "Dashboard",
    icon: Home,
    end: true,
  });

  if (canReadService(role, ServiceDomain.FeatureFlags)) {
    items.push({
      key: "flags",
      to: Constants.FEATURE_FLAGS_ROUTE,
      label: "Feature flags",
      icon: Flag,
    });
  }

  if (hasPermission(role, Permission.VIEW_APP_VERSION)) {
    items.push({
      key: "app-releases",
      to: Constants.APP_RELEASE_VERSIONS_ROUTE,
      label: "Release",
      icon: Package,
    });
  }

  if (hasPermission(role, Permission.VIEW_MOBILE_TRACKING)) {
    items.push({
      key: "iv3-mobile",
      to: Constants.IV3_MOBILE_ROUTE,
      label: "Mobile",
      icon: Smartphone,
    });
  }

  if (canReadService(role, ServiceDomain.AppFaq)) {
    items.push({
      key: "app-faq",
      to: Constants.APP_FAQ_ROUTE,
      label: "In-app FAQ",
      icon: CircleHelp,
    });
  }

  if (canReadService(role, ServiceDomain.DynamicLinks)) {
    items.push({
      key: "dynamic-links",
      to: Constants.DYNAMIC_LINKS_ROUTE,
      label: "Dynamic links",
      icon: Link2,
    });
  }

  if (canReadService(role, ServiceDomain.HappeningNow)) {
    items.push({
      key: "happening-now",
      to: Constants.HAPPENING_NOW_ROUTE,
      label: "Happening now",
      icon: Sparkles,
    });
  }

  if (canReadService(role, ServiceDomain.Broadcasts)) {
    items.push({
      key: "broadcasts",
      to: Constants.BROADCASTS_ROUTE,
      label: "Broadcasts",
      icon: Megaphone,
    });
  }

  if (canReadService(role, ServiceDomain.PortalNotifications)) {
    items.push({
      key: "portal-notifications",
      to: Constants.PORTAL_NOTIFICATIONS_ROUTE,
      label: "Portal notifications",
      icon: Bell,
    });
  }

  if (canReadService(role, ServiceDomain.ImageHosting)) {
    items.push({
      key: "image-hosting",
      to: Constants.IMAGE_HOSTING_ROUTE,
      label: "Image hosting",
      icon: ImageUp,
    });
  }

  return items;
}
